import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { PharmacyInformationService } from './services/pharmacy-information.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { StateStructure } from '@ptl/state';
import {
  addPharmacy,
  removePharmacys,
  PharmacyState,
  PHARMACY_REDUX_STATE_ID,
  selectPharmacy
} from '../pharmacy-information/configs/reducers/pharmacy.reducer';
import { NgRedux } from '@ptl/angular-redux-store';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PharmacyLookupAutoCompleteResponse } from './configs/interfaces/pharmacy-lookup/response/response.interface';
import { UntypedFormBuilder, Form, UntypedFormGroup, AbstractControl } from '@angular/forms';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { Pharmacy } from './configs/interfaces/pharmacy.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { debounceTime, tap } from 'rxjs/operators';

export interface PharmacyInformationFormAsJson {
  radius: number;
  zipCode: string;
  searchString: string;
}

export interface PharmacyInformationFormControls {
  radius: AbstractControl;
  zipCode: AbstractControl;
  searchString: AbstractControl;
}


export interface PharmacyInformationFormAsJson {
  radius: number;
  zipCode: string;
  searchString: string;
}

export interface PharmacyInformationFormControls {
  radius: AbstractControl;
  zipCode: AbstractControl;
  searchString: AbstractControl;
}

export class HttpError {
  static InternalServerError = 500;
}

@Component({
    selector: 'app-pharmacy-information',
    templateUrl: './pharmacy-information.component.html',
    styleUrls: ['./pharmacy-information.component.scss'],
    standalone: false
})
export class PharmacyInformationComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger, static: true })
  inputAutoComplit: MatAutocompleteTrigger;

  @Input() title = 'Find Pharmacies';
  @Input() subTitle = '(Optional)';
  @Input() description = 'Search for and add your pharmacies to refine your plan results';
  @Input() maxPharmacys = 1;
  @Input() defaultRadius: string;

  disableSubmit: boolean;
  showSpinner: boolean;
  broadSearch: boolean;
  minimumCharError: boolean = false;
  radius = [
    { id: 5, value: '5 miles' },
    { id: 10, value: '10 miles' },
    { id: 20, value: '20 miles' }
  ];

  autoCompleteResults = [];

  form: UntypedFormGroup;
  lastZipCodeQueried: string;
  angularHostUrl: string;

  constructor(
    private fb: UntypedFormBuilder,
    private pharmacyService: PharmacyInformationService,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService
  ) {
  }

  get formControls(): PharmacyInformationFormControls {
    return this.form as unknown as PharmacyInformationFormControls;
  }

  get formValuesAsJson(): PharmacyInformationFormAsJson {
    return this.form.value as unknown as PharmacyInformationFormAsJson;
  }

  selectedPharmacys: Pharmacy[] = [];
  showNoResults: Boolean = false;

  ngOnInit() {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');

    this.form = this.fb.group({
      radius: this.fb.control(this.defaultRadius ? +this.defaultRadius : 10),
      zipCode: this.fb.control(zipCountyState.zipCode),
      searchString: this.fb.control(''),
    });

    this.angularHostUrl = environment.infrastructure.googleCloud.angularHostUrl;
    this.showSpinner = false;

    const selectedPharmacysState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);

    Array.prototype.push.apply(this.selectedPharmacys, selectedPharmacysState.pharmacys);

    this.form
      .valueChanges.pipe(
        debounceTime(500),
        tap((searchValues: PharmacyInformationFormAsJson) => {
          if (searchValues.searchString.length > 2) {
            this.minimumCharError = false;
            this.filterPharmacysAutoComplete();
          } else {
            this.inputAutoComplit.closePanel();
            this.minimumCharError = true;
          }
        })
      ).subscribe()
  }

  /**
   * Add Pharmacy to selectedPharmacys[]
   * @param addedPharmacy Pharmacy to be added to selectedPharmacys[]
   */
  async addPharmacy(addedPharmacy: Pharmacy): Promise<void> {
    // set a flag on the results to disable the add button for selected pharmacies
    addedPharmacy.isSelected = true;
    this.selectedPharmacys.push(addedPharmacy);
    
    this.inputAutoComplit.closePanel();
    this.ngRedux.dispatch(addPharmacy(addedPharmacy));
    
    // Update selected pharmacy if we don't have one selected yet just so we have a default
    const selectedPharmacysState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);
    if (!selectedPharmacysState.selectedPharmacy) {
      this.ngRedux.dispatch(selectPharmacy(addedPharmacy));
    }
  }

  /**
   * remove pharmacy from selectedPharmacys[]
   * @param pharmacy pharmacy to be removed from selectedPharmacys[]
   */
  removePharmacy(pharmacy: Pharmacy): void {
    // remove the pharmacy from list of pharmacys to search in (to prevent being selected more than once)
    const pharmacyIndex = this.selectedPharmacys.indexOf(pharmacy);
    this.selectedPharmacys.splice(pharmacyIndex, 1);

    // reset selected object back to false. if it's still in autocomplete results it will reactivate
    const pharmResult = this.autoCompleteResults.filter((result) => pharmacy.pharmacyId === result.pharmacyId);
    if (pharmResult[0]) {
      pharmResult[0].isSelected = false;
    }

    this.ngRedux.dispatch(removePharmacys(pharmacy));

    // remove selected pharmacy if the list is empty
    if (this.selectedPharmacys.length == 0) {
      this.ngRedux.dispatch(selectPharmacy(null));
    }
  }

  async filterPharmacysAutoComplete(): Promise<any> {
    this.broadSearch = false;
    this.showNoResults = false;
    this.showSpinner = true;
    this.disableSubmit = true;
    this.autoCompleteResults = [];
    const formValues: PharmacyInformationFormAsJson = this.formValuesAsJson;

    try {
      /** Request Pharmacy Info */
      const response: PharmacyLookupAutoCompleteResponse = await this.pharmacyService.byPharmacyNameAutoComplete(
        formValues.searchString,
        formValues.radius,
        formValues.zipCode
      );

      if (!response?.PharmacyList) {
        this.showNoResults = true;
        return;
      }

      const arrayPharmacys = response.PharmacyList;

      for (const pharmacy of arrayPharmacys) {

        // Set is selected flag for any results that are already selected
        const isPharmacyAlreadySelected
          = this.selectedPharmacys.filter(pharm => {
            return pharm.pharmacyId === pharmacy.PharmacyID;
          }).length > 0;

        this.autoCompleteResults.push({
          name: pharmacy.Name,
          address1: pharmacy.Address1,
          address2: pharmacy.Address2,
          city: pharmacy.City,
          state: pharmacy.State,
          pharmacyId: pharmacy.PharmacyID,
          pharmacyNpi: pharmacy.PharmacyNPI,
          isSelected: isPharmacyAlreadySelected
        });
      }

      this.inputAutoComplit.openPanel();

      // After searching once, we can show the no results message (if applicable)
      this.showNoResults = true;

    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        switch (e.status) {
          case HttpError.InternalServerError:
            this.broadSearch = true;
            break;
        }
      }
      console.error(e);
    }

    this.showSpinner = false;
    this.disableSubmit = false;
  }
}
